import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env/environment";
import { EnvironmentName } from "@env/environment.interface";
import { Observable, OperatorFunction, Subject, catchError, mergeMap, of, pipe, takeUntil, throwError } from "rxjs";
import { GLOBAL_REGEX } from "../constants/global.regex";
import { getApiUrl } from "../helpers/get-api-url";
import { NotyService } from "../helpers/zorro-notification.helper";
import { HttpResponse } from "./http-response.interface";
import { HttpStatus } from "./http-status.enum";
import { StatusType } from "./status-type.enum";

@Injectable({ providedIn: "root" })
export class ApiService {
	private $unsubscribe = new Subject<void>();
	private API_URL = getApiUrl();

	constructor(
		private http: HttpClient,
		private notyService: NotyService,
	) {
		if (environment.environmentName === EnvironmentName.LOCAL) {
			this.API_URL = environment.apiBaseUrl;
		}
	}

	get<T = any>(path: string, params = {}, handleErrors = true): Observable<HttpResponse<T>> {
		const sanitizedParams = this.sanitizeData(params);
		return this.http
			.get<HttpResponse<T>>(`${this.API_URL}${path}`, { params: sanitizedParams })
			.pipe(takeUntil(this.$unsubscribe), this.processRequest(handleErrors));
	}

	put<T = any>(path: string, body = {}, params = {}, handleErrors = true): Observable<HttpResponse<T>> {
		const sanitizedBody = this.sanitizeData(body);
		const sanitizedParams = this.sanitizeData(params);
		return this.http
			.put<HttpResponse<T>>(`${this.API_URL}${path}`, sanitizedBody, { params: sanitizedParams })
			.pipe(takeUntil(this.$unsubscribe), this.processRequest(handleErrors));
	}

	post<T = any>(path: string, body = {}, params = {}, handleErrors = true): Observable<HttpResponse<T>> {
		const sanitizedBody = this.sanitizeData(body);
		const sanitizedParams = this.sanitizeData(params);
		return this.http
			.post<HttpResponse<T>>(`${this.API_URL}${path}`, sanitizedBody, { params: sanitizedParams })
			.pipe(takeUntil(this.$unsubscribe), this.processRequest(handleErrors));
	}

	delete<T = any>(path: string, params = {}, handleErrors = true): Observable<HttpResponse<T>> {
		const sanitizedParams = this.sanitizeData(params);
		return this.http.delete<HttpResponse<T>>(`${this.API_URL}${path}`, { params: sanitizedParams }).pipe(this.processRequest(handleErrors));
	}

	tearDown(): void {
		this.$unsubscribe.next();
	}

	private sanitizeData(data: any): any {
		if (data === null || data === undefined) {
			return data;
		}

		if (
			data instanceof FormData ||
			data instanceof File ||
			data instanceof Blob ||
			data instanceof ArrayBuffer ||
			ArrayBuffer.isView(data) ||
			data instanceof Date ||
			data instanceof Map ||
			data instanceof Set ||
			data instanceof FileList
		) {
			return data;
		}

		if (typeof data === "string") {
			const trimmed = data.trim();
			return this.sanitizeString(trimmed);
		}

		if (Array.isArray(data)) {
			return data.map((item) => this.sanitizeData(item));
		}

		if (typeof data === "object") {
			const sanitized: any = {};
			for (const key in data) {
				if (Object.prototype.hasOwnProperty.call(data, key)) {
					sanitized[key] = this.sanitizeData(data[key]);
				}
			}
			return sanitized;
		}

		return data;
	}

	private sanitizeString(str: string): string {
		return str.normalize(GLOBAL_REGEX.NORMALIZATION_FORM).replace(GLOBAL_REGEX.DIACRITICS_SANITIZATION, "");
	}

	private processRequest<T>(handleError = true): OperatorFunction<HttpResponse<T>, HttpResponse<T>> {
		return pipe(
			mergeMap((res: HttpResponse<T>) => {
				if (res.status !== HttpStatus.SUCCESS) {
					if (handleError) {
						this.processRequestMessage(res.status, res.responseMessage);
					}
					return throwError(() => res);
				}
				return of(res);
			}),
			catchError((err) => {
				if (handleError) {
					this.processRequestMessage(err?.error?.status, err?.error?.responseMessage);
				}
				return of(err?.error ? err?.error : err);
			}),
		);
	}

	private processRequestMessage(status: HttpStatus, responseMessage: { message: string; title: string; type: StatusType }): void {
		switch (responseMessage?.type) {
			case StatusType.DEBUG:
				this.notyService.error("Error", "Something went wrong! Please try again later.");
				break;
			case StatusType.TOAST:
				this.notyService.error(responseMessage.title, responseMessage.message);
				break;
			case StatusType.INTERFACE:
				break;
		}
	}
}
